import { useId } from 'react';
import ReactSelect from 'react-select';
import { useTranslation } from 'react-i18next';
import { cn } from '../../utils/cn';

/**
 * SearchableSelect — select con búsqueda (typeahead) alineado al design system.
 *
 * Envuelve `react-select` en modo `unstyled` + `classNames` con clases Tailwind
 * basadas en tokens (bg/fg/border/brand), por lo que respeta dark mode sin
 * estilos extra. El menú se renderiza en un portal a `document.body`
 * (`menuPosition="fixed"`) para no quedar recortado por contenedores con
 * overflow (Drawer/Modal) — ver regla de dropdowns clippeados.
 *
 * API estilo `ui/Select` (drop-in): value/onChange trabajan con el VALOR
 * (string), no con el objeto opción.
 *
 * Props:
 *   options      [{ value, label }] o agrupadas: [{ label, options: [{ value, label }] }]
 *   value        valor seleccionado (string) — '' / null = sin selección
 *   onChange     (value: string) => void
 *   label,hint,error, placeholder, isClearable, isDisabled, id
 *   noOptionsText texto cuando no hay coincidencias (default 'Sin resultados')
 *   controlClassName clases extra del control (se combinan con twMerge; p. ej.
 *                texto de 16 px en móvil para que iOS no haga zoom al enfocar)
 */
export function SearchableSelect({
  label,
  hint,
  error,
  options = [],
  value,
  onChange,
  placeholder,
  isClearable = false,
  isDisabled = false,
  noOptionsText = 'Sin resultados',
  id,
  controlClassName,
  ...rest
}) {
  const { t } = useTranslation();
  const generatedId = useId();
  const inputId = id ?? generatedId;
  // react-select trata como grupo a toda opción con `options`: el valor elegido
  // se busca en la lista aplanada. Sin grupos, la lista queda igual que antes.
  const selected =
    options
      .flatMap((o) => (Array.isArray(o?.options) ? o.options : [o]))
      .find((o) => String(o.value) === String(value ?? '')) ?? null;

  return (
    <div className="flex flex-col gap-1">
      {label && (
        <label htmlFor={inputId} className="text-sm font-medium text-fg">
          {label}
        </label>
      )}
      <ReactSelect
        inputId={inputId}
        unstyled
        isClearable={isClearable}
        isDisabled={isDisabled}
        options={options}
        value={selected}
        onChange={(opt) => onChange?.(opt ? opt.value : '')}
        placeholder={placeholder}
        noOptionsMessage={() => t(noOptionsText)}
        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
        menuPosition="fixed"
        menuShouldScrollIntoView={false}
        classNames={{
          control: ({ isFocused }) =>
            cn(
              'flex items-center min-h-[40px] rounded-lg border bg-bg text-sm transition-colors',
              isFocused
                ? 'border-transparent ring-2 ring-brand-500'
                : error
                  ? 'border-status-error'
                  : 'border-border',
              isDisabled && 'opacity-50 cursor-not-allowed',
              controlClassName,
            ),
          valueContainer: () => 'flex flex-wrap items-center gap-0.5 pl-3 pr-1 grow',
          placeholder: () => 'text-fg-muted',
          singleValue: () => 'text-fg',
          input: () => 'text-fg [&_input]:focus:ring-0 [&_input]:shadow-none',
          indicatorsContainer: () => 'flex items-center text-fg-muted',
          dropdownIndicator: () => 'px-1.5 [&>svg]:w-4 [&>svg]:h-4',
          clearIndicator: () => 'px-1 cursor-pointer hover:text-fg [&>svg]:w-4 [&>svg]:h-4',
          indicatorSeparator: () => 'hidden',
          menu: () =>
            'mt-1 rounded-lg border border-border bg-bg shadow-elevated overflow-hidden',
          menuList: () => 'py-1 max-h-60 overflow-y-auto',
          // Solo se renderizan con opciones agrupadas.
          group: () => 'py-1',
          groupHeading: () =>
            'px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-fg-muted',
          option: ({ isFocused, isSelected }) =>
            cn(
              'px-3 py-2 text-sm cursor-pointer',
              isSelected
                ? 'bg-brand-500 text-white'
                : isFocused
                  ? 'bg-brand-50 text-brand-700'
                  : 'text-fg',
            ),
          noOptionsMessage: () => 'px-3 py-2 text-sm text-fg-muted',
        }}
        styles={{ menuPortal: (base) => ({ ...base, zIndex: 10000 }) }}
        {...rest}
      />
      {error ? (
        <span className="text-xs text-status-error">{error}</span>
      ) : hint ? (
        <span className="text-xs text-fg-muted">{hint}</span>
      ) : null}
    </div>
  );
}

export default SearchableSelect;
